import {
  createContext,
  createEffect,
  onCleanup,
  type ParentComponent,
  useContext,
} from "solid-js";
import { getClaimsClient } from "~/lib/api/claims";
import { getCorrelationClient } from "~/lib/api/correlation";
import { getProjectClient } from "~/lib/api/project";
import { getSourceClient } from "~/lib/api/source";
import { useAuth } from "~/lib/auth/auth-context";

type CacheEntry = {
  token: string;
  expiresAt: Date;
  timer: ReturnType<typeof setTimeout>;
};

export class TokenExchangeManager {
  private generation = 0;
  private projectCache = new Map<string, CacheEntry>();
  private sourceCache = new Map<string, CacheEntry>();
  private projectInflight = new Map<string, Promise<string>>();
  private sourceInflight = new Map<string, Promise<string>>();

  constructor(private readonly getKeycloakToken: () => string | null) {}

  async projectClient(projectId: string) {
    const token = await this.resolveProjectToken(projectId);
    return getProjectClient(token);
  }

  async sourceClient(projectId: string) {
    const token = await this.resolveProjectToken(projectId);
    return getSourceClient(token);
  }

  async correlationClient(projectId: string) {
    const token = await this.resolveProjectToken(projectId);
    return getCorrelationClient(token);
  }

  async claimsClient(projectId: string, sourceId: string) {
    const token = await this.resolveSourceToken(projectId, sourceId);
    return getClaimsClient(token);
  }

  clearAll() {
    this.generation++;
    for (const entry of this.projectCache.values()) clearTimeout(entry.timer);
    for (const entry of this.sourceCache.values()) clearTimeout(entry.timer);
    this.projectCache.clear();
    this.sourceCache.clear();
    this.projectInflight.clear();
    this.sourceInflight.clear();
  }

  private async resolveProjectToken(projectId: string): Promise<string> {
    const cached = this.projectCache.get(projectId);
    if (cached && cached.expiresAt > new Date()) return cached.token;

    const inflight = this.projectInflight.get(projectId);
    if (inflight) return inflight;

    const keycloakToken = this.getKeycloakToken();
    if (!keycloakToken) throw new Error("Not authenticated");

    const promise = (async (): Promise<string> => {
      const gen = this.generation;
      const client = getProjectClient(keycloakToken);
      const response = await client.getProjectToken({ projectId });
      if (this.generation !== gen) return response.projectToken;
      const token = response.projectToken;
      const expiresAt =
        response.expiresAt?.toDate() ?? new Date(Date.now() + 3_600_000);
      const delayMs = Math.max(0, expiresAt.getTime() - Date.now() - 60_000);
      const timer = setTimeout(async () => {
        this.projectCache.delete(projectId);
        const kt = this.getKeycloakToken();
        if (!kt) return;
        try {
          await this.resolveProjectToken(projectId);
        } catch {
          /* silent */
        }
      }, delayMs);
      this.projectCache.set(projectId, { token, expiresAt, timer });
      return token;
    })();

    this.projectInflight.set(projectId, promise);
    try {
      return await promise;
    } finally {
      this.projectInflight.delete(projectId);
    }
  }

  private async resolveSourceToken(
    projectId: string,
    sourceId: string,
  ): Promise<string> {
    const cacheKey = `${projectId}:${sourceId}`;
    const cached = this.sourceCache.get(cacheKey);
    if (cached && cached.expiresAt > new Date()) return cached.token;

    const inflight = this.sourceInflight.get(cacheKey);
    if (inflight) return inflight;

    const promise = (async (): Promise<string> => {
      const gen = this.generation;
      const projectToken = await this.resolveProjectToken(projectId);
      const client = getSourceClient(projectToken);
      const response = await client.getSourceToken({ sourceId });
      if (this.generation !== gen) return response.sourceToken;
      const token = response.sourceToken;
      const expiresAt =
        response.expiresAt?.toDate() ?? new Date(Date.now() + 3_600_000);
      const delayMs = Math.max(0, expiresAt.getTime() - Date.now() - 60_000);
      const timer = setTimeout(async () => {
        this.sourceCache.delete(cacheKey);
        try {
          await this.resolveSourceToken(projectId, sourceId);
        } catch {
          /* silent */
        }
      }, delayMs);
      this.sourceCache.set(cacheKey, { token, expiresAt, timer });
      return token;
    })();

    this.sourceInflight.set(cacheKey, promise);
    try {
      return await promise;
    } finally {
      this.sourceInflight.delete(cacheKey);
    }
  }
}

const TokenExchangeContext = createContext<TokenExchangeManager>();

export const TokenExchangeProvider: ParentComponent = (props) => {
  const auth = useAuth();
  const manager = new TokenExchangeManager(() => auth.accessToken());

  createEffect(() => {
    // Track accessToken reactively; clear all caches when it changes
    auth.accessToken();
    // We run clearAll on every change, including the first render.
    // That's fine — the cache is empty on first render anyway.
    manager.clearAll();
  });

  onCleanup(() => manager.clearAll());

  return (
    <TokenExchangeContext.Provider value={manager}>
      {props.children}
    </TokenExchangeContext.Provider>
  );
};

export function useTokenExchange(): TokenExchangeManager {
  const ctx = useContext(TokenExchangeContext);
  if (!ctx)
    throw new Error(
      "useTokenExchange must be used within TokenExchangeProvider",
    );
  return ctx;
}
